import React, { useContext, useEffect, useState, useRef } from "react"
import { AppContext } from "../components/context/AppContext"
import { Formik, Form, Field } from "formik"
import { TextInput } from "../components/forms"
import { Button, ErrorMessageStyled, submissionStatusStyles as styles } from "../components/styled"
import { SubmissionApi, AuthApi } from "../utils/api"
import { LoadingSpinner } from "../components/loadingSpinner"
import { validatePhone } from "../utils/Helpers"
import { RebateList } from "../components/RebateList"

const formatPhone = (phone) => {
  const digits = String(phone).replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

// Screen-reader-friendly version: digits separated by spaces so NVDA reads them
// one at a time ("four eight zero ...") instead of "left paren four eight zero
// right paren ... dash ...". Used in aria-label only.
const formatPhoneForScreenReader = (phone) => {
  const digits = String(phone).replace(/\D/g, "")
  if (digits.length === 10) {
    return `${digits.slice(0, 3).split("").join(" ")}, ${digits.slice(3, 6).split("").join(" ")}, ${digits.slice(6).split("").join(" ")}`
  }
  return phone
}

export const SubmissionStatus = () => {
  const { state, dispatch } = useContext(AppContext)
  const [eligibleLines, setEligibleLines] = useState([])
  const [isFetchingSubmissions, setIsFetchingSubmissions] = useState(false)
  const [tfbError, setTfbError] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedLine, setSelectedLine] = useState("")
  const [tfbDataLoaded, setTfbDataLoaded] = useState(false)
  const [isLineOpen, setIsLineOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dataRef = useRef(null)
  const lineRef = useRef(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (!isLineOpen) {
      setHighlightedIndex(-1)
      return
    }
    const handleClickOutside = (e) => {
      if (lineRef.current && !lineRef.current.contains(e.target)) {
        setIsLineOpen(false)
      }
    }
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsLineOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("touchstart", handleClickOutside)
    document.addEventListener("keydown", handleEsc)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("touchstart", handleClickOutside)
      document.removeEventListener("keydown", handleEsc)
    }
  }, [isLineOpen])

  useEffect(() => {
    if (!isLineOpen || highlightedIndex < 0 || !dropdownRef.current) return
    const item = dropdownRef.current.children[highlightedIndex]
    if (item) item.scrollIntoView({ block: "nearest" })
  }, [highlightedIndex, isLineOpen])

  const buildRequestData = () => ({
    UserInfo: "",
    PhoneNumber: "",
    IsTFBAccount: state.user.isTFBAccount,
  })

  useEffect(() => {
    if (state.user.isTFBAccount) return
    const init = async () => {
      try {
        const submissions = await fetchAllSubmissions(buildRequestData())
        const response = await AuthApi.getEligiblesLines(buildRequestData())
        const lines = response.data || []
        setEligibleLines(lines)
        if (lines.length > 0) {
          setSelectedLine(lines[0])
          applyMsisdnFilter(lines[0], submissions)
        }
      } catch {
        setErrorMessage("Something went wrong. Please try again.")
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAllSubmissions = async (reqData) => {
    setIsFetchingSubmissions(true)
    try {
      const submissions = await SubmissionApi.getAllPreviousSubmissionsByBan(reqData)
      dispatch({ type: "SETSUBMISSIONS", payload: submissions })
      dataRef.current = submissions
      return submissions
    } finally {
      setIsFetchingSubmissions(false)
    }
  }

  const applyMsisdnFilter = (phoneNumber, submissions) => {
    const src = submissions || dataRef.current || state.submissions
    if (!src) return
    const match = (s) => s.confirmMIN === phoneNumber
    const filtered = [
      ...src.iSubmissions.filter(match),
      ...src.submissions.filter(match),
    ]
    dispatch({ type: "SETFILTEREDSUBMISSIONSBYMSISDN", payload: filtered })
  }

  const handleLineChange = (phone) => {
    setSelectedLine(phone)
    applyMsisdnFilter(phone, dataRef.current)
    setIsLineOpen(false)
  }

  const openDropdown = () => {
    setIsLineOpen(true)
    const currentIdx = eligibleLines.indexOf(selectedLine)
    setHighlightedIndex(currentIdx >= 0 ? currentIdx : 0)
  }

  const handleLineKeyDown = (e) => {
    if (eligibleLines.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      if (!isLineOpen) openDropdown()
      else setHighlightedIndex((idx) => Math.min(idx + 1, eligibleLines.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      if (!isLineOpen) openDropdown()
      else setHighlightedIndex((idx) => Math.max(idx - 1, 0))
    } else if (e.key === "Enter" || e.key === " ") {
      if (isLineOpen && highlightedIndex >= 0 && highlightedIndex < eligibleLines.length) {
        e.preventDefault()
        handleLineChange(eligibleLines[highlightedIndex])
      }
    } else if (e.key === "Home" && isLineOpen) {
      e.preventDefault()
      setHighlightedIndex(0)
    } else if (e.key === "End" && isLineOpen) {
      e.preventDefault()
      setHighlightedIndex(eligibleLines.length - 1)
    }
  }

  const handleTfbSubmit = async (values, { setSubmitting }) => {
    try {
      setTfbError("")
      const phone = values.tfbPhoneNumber
      const tfbRequestData = { ...state.user, primaryPhoneNumber: phone }
      const user = await AuthApi.setTfbUserInfo(tfbRequestData)
      if (!user) {
        setTfbError("The phone number you entered was not found. Please try again")
        dispatch({ type: "SETSUBMISSIONS", payload: "" })
        dispatch({ type: "SETFILTEREDSUBMISSIONSBYMSISDN", payload: "" })
        return
      }
      dispatch({ type: "SETUSER", payload: user })
      const reqData = { UserInfo: state.user.userInfo, PhoneNumber: phone, IsTFBAccount: true }
      const submissions = await fetchAllSubmissions(reqData)
      setSelectedLine(phone)
      applyMsisdnFilter(phone, submissions)
      setTfbDataLoaded(true)
    } catch {
      setTfbError("Something went wrong. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const rebates = state.filteredSubmissionsByMsisdn || []
  const userName = state.user.firstName || ""
  const showRebateList = !isFetchingSubmissions && ((!state.user.isTFBAccount && selectedLine) || (state.user.isTFBAccount && tfbDataLoaded))

  return (
    <div className="container hero3">
      {/* ── Header ── */}
      <div className="row">
        <div className="col-md-12">
          <h1 className="magenta">Rebates status</h1>
          <p>Select a line to view the status of your rebates.</p>
        </div>
      </div>

      {/* ── Line Selector (regular accounts) ── */}
      {!state.user.isTFBAccount && (
        <div className="row" style={{ marginTop: 24 }}>
          <div className="col-md-12 col-lg-6">
            <div ref={lineRef} style={styles.lineFieldset}>
              <span style={styles.lineFloatLabel}>Line</span>
              <button
                type="button"
                onClick={() => {
                  if (eligibleLines.length === 0) return
                  if (isLineOpen) setIsLineOpen(false)
                  else openDropdown()
                }}
                onKeyDown={handleLineKeyDown}
                style={styles.lineSelect}
                aria-haspopup="listbox"
                aria-expanded={isLineOpen}
                aria-activedescendant={isLineOpen && highlightedIndex >= 0 ? `line-option-${highlightedIndex}` : undefined}
                aria-label={
                  selectedLine
                    ? `Line, ${userName ? userName + ", " : ""}phone number ${formatPhoneForScreenReader(selectedLine)}`
                    : "Select a phone line"
                }
                disabled={eligibleLines.length === 0}
              >
                {selectedLine
                  ? (userName ? `${userName} \u2022 ${formatPhone(selectedLine)}` : formatPhone(selectedLine))
                  : "Select a line"}
              </button>
              <span style={styles.selectArrow} aria-hidden="true">&#9660;</span>
              {isLineOpen && (
                <ul ref={dropdownRef} style={styles.lineDropdownPanel} role="listbox">
                  {eligibleLines.map((line, idx) => {
                    const isActive = selectedLine === line
                    const isHighlighted = idx === highlightedIndex
                    return (
                      <li
                        key={line}
                        id={`line-option-${idx}`}
                        role="option"
                        aria-selected={isActive}
                        aria-label={`${userName ? userName + ", " : ""}phone number ${formatPhoneForScreenReader(line)}`}
                        style={{
                          ...styles.lineDropdownOption,
                          ...(isActive ? styles.lineDropdownOptionActive : {}),
                          ...(isHighlighted ? styles.lineDropdownOptionHighlighted : {}),
                        }}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        onClick={() => handleLineChange(line)}
                      >
                        {userName ? `${userName} \u2022 ${formatPhone(line)}` : formatPhone(line)}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Line Selector (TFB accounts) ── */}
      {state.user.isTFBAccount && (
        <Formik initialValues={{ tfbPhoneNumber: "" }} onSubmit={handleTfbSubmit}>
          {({ isSubmitting, errors }) => (
            <Form>
              <div className="row" style={{ marginTop: 24 }}>
                <div className="form-group col-md-6">
                  <label htmlFor="tfbPhoneNumber">Enter your primary phone number *</label>
                  <Field
                    id="tfbPhoneNumber"
                    name="tfbPhoneNumber"
                    type="text"
                    component={TextInput}
                    validate={validatePhone}
                    aria-required="true"
                  />
                </div>
              </div>
              {tfbError && <ErrorMessageStyled role="alert">{tfbError}</ErrorMessageStyled>}
              <div style={{ marginTop: 8 }}>
                <Button
                  type="submit"
                  className="btn"
                  primary
                  disabled={isSubmitting || Object.keys(errors).length > 0}
                >
                  {isSubmitting ? "Loading…" : "Continue"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}

      {errorMessage && <ErrorMessageStyled role="alert">{errorMessage}</ErrorMessageStyled>}

      {/* ── Loading spinner ── */}
      {isFetchingSubmissions && (
        <div className="row" style={{ marginTop: 24 }}>
          <div className="col-md-12" style={{ textAlign: "center" }}>
            <LoadingSpinner />
            <h2 className="magenta">Loading…</h2>
          </div>
        </div>
      )}

      {/* ── Rebates list ── */}
      {showRebateList && <RebateList rebates={rebates} />}
    </div>
  )
}

SubmissionStatus.requiresAuth = true
